import { makeStyles, type Theme } from '@material-ui/core/styles';

import { COLORS } from 'src/data/ui';

import type { CustomSwitchSize } from './types';

const PADDING = 2;

interface SizeProps {
  width: number;
  height: number;
}

const SIZES: Record<CustomSwitchSize, SizeProps> = {
  small: { width: 25, height: 14 },
  medium: { width: 36, height: 20 },
};

const BG_COLOR_DEFAULT = COLORS.gray.gray30;

interface StyleProps {
  size: CustomSwitchSize;
}

export const useStyles = makeStyles<Theme, StyleProps>(
  (theme) => ({
    root: {
      width: ({ size }) => SIZES[size].width,
      height: ({ size }) => SIZES[size].height,
      padding: 0,
    },

    smallSwitchBase: createSwitchBaseStyles('small', theme),
    mediumSwitchBase: createSwitchBaseStyles('medium', theme),

    thumb: {
      width: ({ size }) => getThumbSize(size),
      height: ({ size }) => getThumbSize(size),
      boxShadow: 'none',
    },

    track: {
      borderRadius: ({ size }) => SIZES[size].height / 2,
      border: `2px solid ${BG_COLOR_DEFAULT}`,
      backgroundColor: BG_COLOR_DEFAULT,
      opacity: 0.5,
      transition: theme.transitions.create(['background-color', 'border']),
    },

    checked: {},
    focusVisible: {},
    disabled: {},

    colorPrimary: {
      ...createColorStyles('smallSwitchBase', theme.palette.primary.main, COLORS.primary.light),
      ...createColorStyles('mediumSwitchBase', theme.palette.primary.main, COLORS.primary.light),
    },

    colorSecondary: {
      ...createColorStyles('smallSwitchBase', theme.palette.secondary.main, COLORS.success.light),
      ...createColorStyles('mediumSwitchBase', theme.palette.secondary.main, COLORS.success.light),
    },
  }),
  { name: 'CustomSwitch' }
);

const getThumbSize = (size: CustomSwitchSize) => SIZES[size].height - PADDING * 2;
const getThumbTranslateValue = (width: number) => (width - PADDING * 2) / 2;

const createSwitchBaseStyles = (size: CustomSwitchSize, theme: Theme) => ({
  padding: PADDING,

  '&$checked': {
    transform: `translateX(${getThumbTranslateValue(SIZES[size].width)}px)`,
    color: theme.palette.common.white,

    '& + $track': {
      backgroundColor: COLORS.gray.gray30,
      borderColor: COLORS.gray.gray30,
      opacity: 1,
    },

    '&$disabled': {
      '& + $track': {
        backgroundColor: COLORS.gray.gray60,
        borderColor: COLORS.gray.gray60,
      },
    },
  },

  '&$disabled': {
    color: theme.palette.common.white,

    '& + $track': {
      backgroundColor: COLORS.gray.gray30,
      borderColor: COLORS.gray.gray30,
    },
  },

  '&:hover': {
    backgroundColor: 'transparent',
  },

  '&$focusVisible $thumb': {
    color: COLORS.gray.gray30,
    border: '4px solid #fff',
  },
});

const createColorStyles = (switchBaseClass: string, mainColor: string, disabledColor?: string) => ({
  [`&$${switchBaseClass}`]: {
    '&$checked': {
      '& + $track': {
        backgroundColor: mainColor,
        borderColor: mainColor,
      },

      '&$disabled': {
        '& + $track': {
          backgroundColor: disabledColor,
          borderColor: disabledColor,
        },
      },
    },

    '&$focusVisible $thumb': {
      color: mainColor,
    },
  },
});
